import { FeatureFlag } from '@ps/web-core-enums';
import { select, useFeature } from '@ps/web-core-store';
import { canUseLocalStorage } from '@ps/web-core-utils';
import type { ClassNameProp } from '@ps/web-shared-components';
import { Button, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import TakeSeatIcon from '../../../assets/take-seat.svg';
import useTournamentTakeSeat from '../../hooks/takeSeat/useTournamentTakeSeat';
import { TXTCLI_Take_seat } from '../../translations';
import { ACTIVE_GAMES_LOCAL_STORAGE_KEY } from '../../utils/constants';
import { getTakeSeatGame } from '../../utils/utils';

import styles from './TakeSeatButton.module.scss';

const TakeSeatButton: React.FC<Partial<ClassNameProp>> = ({ className }) => {
  const enabledActiveGamesFeature = useFeature(FeatureFlag.PKRNavigationActiveTableButton);
  const games = useSelector(select.activeGames.getGames);
  const canTakeSeatGame = getTakeSeatGame(games);
  const openedActiveGames: number[] = canUseLocalStorage
    ? (JSON.parse(localStorage.getItem(ACTIVE_GAMES_LOCAL_STORAGE_KEY) ?? '[]') as number[])
    : [];
  const isButtonAlreadyClicked = openedActiveGames.includes(Number(canTakeSeatGame?.id));
  const takeSeat = useTournamentTakeSeat(Number(canTakeSeatGame?.id));
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [showButton, setShowButton] = useState(false);
  const [showHideAnimation, setShowHideAnimation] = useState(false);

  const showTakeSeatButton = useCallback(() => {
    setShowHideAnimation(false);
    timeoutRef.current = setTimeout(() => {
      setShowButton(true);
    }, 1000);
  }, []);

  const hideTakeSeatButton = useCallback(() => {
    setShowHideAnimation(true);
    timeoutRef.current = setTimeout(() => {
      // Hide the button after the animation duration
      setShowButton(false);
    }, 1000);
  }, []);

  const handleClick = useCallback(() => {
    takeSeat();
    hideTakeSeatButton();
  }, [hideTakeSeatButton, takeSeat]);

  useEffect(() => {
    if (enabledActiveGamesFeature && canTakeSeatGame && !isButtonAlreadyClicked) {
      showTakeSeatButton();
    } else if (showButton) {
      hideTakeSeatButton();
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    canTakeSeatGame,
    enabledActiveGamesFeature,
    hideTakeSeatButton,
    isButtonAlreadyClicked,
    showButton,
    showTakeSeatButton
  ]);

  if (!showButton) {
    return null;
  }

  return (
    <div
      className={classNames(styles.wrap, className, {
        [styles.hideAnimation]: showHideAnimation
      })}
    >
      <TakeSeatIcon />
      <p className={styles.text}>
        <Translation {...TXTCLI_Take_seat} />
      </p>
      <Button
        className={styles.takeSeatButton}
        type="button"
        translation={TXTCLI_Take_seat}
        onClick={handleClick}
      />
    </div>
  );
};
export default TakeSeatButton;
